import "./DebugHome.scss";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse, faChartLine } from "@fortawesome/free-solid-svg-icons";
import HeatMapGrid from "../../components/HeatMapGrid";
import ReactApexChart from "react-apexcharts";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
} from "recharts";

const DebugHome = () => {
  const location = useLocation();
  const product = location.state?.product;
  const [fetchingData, setFetchingData] = useState(false);
  const [fetchingChartData, setFetchingChartData] = useState(false);
  const [showViewChartButton, setShowViewChartButton] = useState(false);
  const [dataAvailable, setDataAvailable] = useState(false);
  const [heatMapData, setHeatMapData] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [selectedDataType, setSelectedDataType] = useState("0"); // State for selected data type (0=Events, 1=Mode changes, 2=Charge Stage changes, 3=Generator Status changes, 4=AC Status changes)
  const [selectedView, setSelectedView] = useState("0"); // State for selected view (0=Heatmap, 1=Timeline)
  const [LogDataArray, setLogDataArray] = useState([]);
  const [showChart, setShowChart] = useState(false);
  const [apexTimelineData, setApexTimelineData] = useState([]); // Store the chart rows
  const [selectedYAxisValues, setSelectedYAxisValues] = useState({}); // State for selected Y-axis values
  const [axisSelection, setAxisSelection] = useState({});
  const [showEvents, setShowEvents] = useState(true);
  const [showValueDots, setShowValueDots] = useState(false);
  const [leftAxisDomain, setLeftAxisDomain] = useState(["auto", "auto"]);
  const [rightAxisDomain, setRightAxisDomain] = useState(["auto", "auto"]);
  const [zoomRangeStart, setZoomRangeStart] = useState("dataMin");
  const [zoomRangeEnd, setZoomRangeEnd] = useState("dataMax");
  const [selectedRows, setSelectedRows] = useState([]); // State to keep track of selected rows
  const [selectionMessage, setSelectionMessage] = useState(""); // State to keep track of selection message
  const [buttonsVisible, setButtonsVisible] = useState(false); // Control button visibility
  const [buttonsEnabled, setButtonsEnabled] = useState(false); // Control button activation

  // #region ApexCharts
  const [apexTimelineOptions, setApexChartOptions] = useState({
    chart: {
      type: "rangeBar",
      zoom: {
        enabled: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "20%",
        rangeBarGroupRows: true,
      },
    },
    xaxis: {
      type: "datetime",
      title: {
        text: "Time",
      },
    },
    yaxis: {
      title: {
        hide: true,
      },
    },
    stroke: {
      width: 1,
    },
    colors: [
      "#008FFB",
      "#00E396",
      "#FEB019",
      "#FF4560",
      "#775DD0",
      "#3F51B5",
      "#546E7A",
      "#D4526E",
      "#8D5B4C",
      "#F86624",
      "#D7263D",
      "#1B998B",
      "#2E294E",
      "#F46036",
      "#E2C044",
    ],
    legend: {
      position: "bottom",
    },
    fill: {
      type: "solid",
    },
  });
  // #endregion ApexCharts

  // #region Constants
  const [xAxisDomain, setXAxisDomain] = useState({
    start: "dataMin",
    end: "dataMax",
  }); // Recharts X Axis domain

  // List of potential variables
  const potentialProperties = [
    "gen_status",
    "gen_freq",
    "gen_volt",
    "grid_status",
    "grid_freq",
    "grid_volt",
    "input_current",
    "input_va",
    "input_watt",
    "output_freq",
    "output_volt",
    "output_current",
    "output_va",
    "output_watt",
    "battery_volt",
    "battery_current",
    "external_current",
    "battery_temp",
    "transformer_temp",
    "busbar_temp",
    "heatsink_temp",
    "fan_speed",
    "mode",
    "error_code",
    "charge_stage",
  ];

  const propertyColors = {
    gen_status: "#FF6384", // Red
    gen_freq: "#FF7675", // Soft Red
    gen_volt: "#FF8C66", // Orange-Red
    grid_status: "#FFA500", // Orange
    grid_freq: "#FFC300", // Yellow-Orange
    grid_volt: "#FFD700", // Yellow
    input_current: "#DFFF00", // Yellow-Green
    input_va: "#9ACD32", // Yellowish Green
    input_watt: "#6B8E23", // Olive Green
    output_freq: "#3CB371", // Medium Sea Green
    output_volt: "#20B2AA", // Light Sea Green
    output_current: "#40E0D0", // Turquoise
    output_va: "#48D1CC", // Medium Turquoise
    output_watt: "#00CED1", // Dark Turquoise
    battery_volt: "#1E90FF", // Dodger Blue
    battery_current: "#4169E1", // Royal Blue
    external_current: "#0000FF", // Blue
    battery_temp: "#00008B", // Dark Blue
    transformer_temp: "#191970", // Midnight Blue
    busbar_temp: "#8A2BE2", // Blue Violet
    heatsink_temp: "#9932CC", // Dark Orchid
    fan_speed: "#9400D3", // Dark Violet
    mode: "#8B008B", // Dark Magenta
    error_code: "#800080", // Purple
    charge_stage: "#6A5ACD", // Slate Blue
  };

  const dataTypeIndexToName = {
    0: "Events",
    1: "Mode changes",
    2: "Charge Stage changes",
    3: "Generator Status changes",
    4: "AC Status changes",
  };

  const modeValues = {
    0: "Standby/Fault",
    1: "Inverter",
    2: "Charger",
    3: "Power Saving",
  };

  const chargeStageValues = {
    0: "standby",
    1: "bulk current",
    2: "absorb voltage",
    3: "equalization voltage",
    4: "floating voltage",
    5: "charging completed",
  };

  const generatorAndAcStatusValues = {
    0: "under frequency",
    1: "over frequency",
    2: "phase lock failure",
    3: "under voltage 1",
    4: "under voltage 2",
    5: "under voltage 3",
    6: "over voltage 1",
    7: "over voltage 2",
    8: "over voltage 3",
    9: "no VAC input",
    10: "input over current",
    11: "reserved",
    12: "reserved",
    13: "reserved",
    14: "reserved",
    15: "phase unlocked",
  };

  // #endregion Constants

  // #region Navigation
  const navigate = useNavigate();
  // Function to handle 'My Products' button click

  const handleNavigateHome = () => {
    navigate("/product");
  };
  // #endregion Navigation

  // #region Fetch data
  useEffect(() => {
    fetchChangeData(); // Fetch data when the component mounts
  }, [selectedDataType, selectedView]); // Dependencies array

  const fetchChangeData = async () => {
    setFetchingData(true); // Show loading message
    setDataAvailable(false); // Reset data availability

    // #region Fetch data
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/changes/${product.id}?type=${selectedDataType}`
      );
      if (!response.ok)
        throw new Error("Failed to fetch timeline and chart data");
      const { data } = await response.json();

      const rawChangeData = Array.from(
        new Set(
          data.map((entry) => {
            const startDate = entry.change_start_date;
            const startTime = entry.change_start_time;
            const endDate = entry.change_end_date;
            const endTime = entry.change_end_time;
            const type = entry.change_type;
            const value = entry.change_value;
            return `${startDate},${startTime},${endDate},${endTime},${type},${value}`;
          })
        )
      ).map((str) => {
        const [startDate, startTime, endDate, endTime, type, value] =
          str.split(",");
        const startDateTime = new Date(`${startDate}T${startTime}`);
        const endDateTime = new Date(`${endDate}T${endTime}`);
        return {
          startDate,
          startTime,
          endDate,
          endTime,
          type,
          value: 1,
          startDateTime,
          endDateTime,
          status: parseInt(type) === 0 ? value : parseInt(value),
        };
      });

      // Now collapse contiguous occurrences with the same value and type
      const uniqueProcessedData = rawChangeData.reduce((acc, curr) => {
        if (acc.length > 0) {
          const prev = acc[acc.length - 1];
          // Check if the current entry has the same value and type as the previous one
          if (prev.status === curr.status && prev.type === curr.type) {
            // Extend the previous element's end date and time to the current one
            prev.endDate = curr.endDate;
            prev.endTime = curr.endTime;
            prev.endDateTime = curr.endDateTime;
          } else {
            // Otherwise, push the current element into the accumulator
            acc.push(curr);
          }
        } else {
          // If the accumulator is empty, push the first element
          acc.push(curr);
        }
        return acc;
      }, []);

      // #endregion Fetch data

      // #region Heatmap data processing

      // Parse data into heat map format
      let dateHourCount = new Map();
      uniqueProcessedData.forEach((entry) => {
        const date = entry.startDate;
        const hour = entry.startTime.split(":")[0];
        const key = `${date},${hour}`;
        const currentCount = dateHourCount.get(key) || 0;
        dateHourCount.set(key, currentCount + 1);
      });

      const heatMapData = Array.from(dateHourCount, ([key, value]) => {
        const [date, hour] = key.split(",");
        return { date, hour, value };
      });
      setHeatMapData(heatMapData);
      // #endregion Heatmap data processing

      // #region Timeline data processing
      const timeLineData = uniqueProcessedData.slice(0,200).map((entry) => {
        // Immediately invoked function to return the name based on the type
        console.log("entry:", JSON.stringify(entry));
        const name = (() => {
          switch (parseInt(entry.type)) {
            case 0:
              return entry.status;
            case 1:
              return modeValues[parseInt(entry.status)];
            case 2:
              return chargeStageValues[parseInt(entry.status)];
            case 3:
            case 4:
              return parseInt(entry.status);
            default:
              return "Unknown";
          }
        })();

        // Mapping entry.type to dataTypeIndexToName
        const x = dataTypeIndexToName[entry.type];

        // Converting date strings to milliseconds
        const yStart = new Date(entry.startDateTime).getTime();
        const yEnd = new Date(entry.endDateTime).getTime();

        return {
          name, // Add the name to the mapped data
          data: [
            {
              x, // x-axis value (like "Mode", "Charge Stage", etc.)
              y: [yStart, yEnd], // y-axis as a range (start and end times in milliseconds)
            },
          ],
        };
      });

      // Set the timeline data to the Apex chart series
      setApexTimelineData(timeLineData);

      // #endregion Timeline data processing

      setDataAvailable(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      setFetchingData(false);
    } finally {
      setFetchingData(false);
    }
  };
  // #endregion Fetch data

  // #region Chart view
  const handleViewChart = async () => {
    try {
      setShowViewChartButton(false);
      setFetchingChartData(true);
      setShowChart(false);
      setShowEvents(true);
      let tempLogDataArray = [];

      if (eventDetails) {
        const date = eventDetails.date;
        const hour = eventDetails.hour;
        const response = await fetch(
          `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/logs/get/data/daterange/${product.id}?date=${date}&hour=${hour}`
        );
        if (response.ok) {
          const dataBody = await response.json();
          const data = dataBody.data;
          tempLogDataArray.push(...data);
        } else {
          console.error("Failed to fetch data");
        }

        // Map the data to add index and sort by time
        tempLogDataArray = tempLogDataArray
          .sort((a, b) => {
            // Assuming 'time' is a string in HH:MM format
            return a.time.localeCompare(b.time);
          })
          .map((item, index) => ({
            ...item,
            index,
          }));

        setLogDataArray(tempLogDataArray); // Update the data
        setXAxisDomain({ start: "dataMin", end: "dataMax" });
        setFetchingChartData(false); // Hide the loading message
        setShowChart(true); // Show the chart
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setFetchingChartData(false);
    }
  };

  useEffect(() => {
    updateAxisDomain();
  }, [selectedYAxisValues, axisSelection]); // Dependencies array

  const renderEventLines = (data) => {
    return data.map((entry, index) => {
      if (entry.event && entry.event.trim() !== "") {
        return (
          <ReferenceLine
            key={`event-${index}`}
            yAxisId="left"
            x={entry.index}
            stroke="grey"
            strokeWidth={4}
            ifLabel={false}
            payload={{ event: entry.event }} // Include event property in the payload
          />
        );
      }
      return null;
    });
  };

  const handleMouseDown = (state) => {
    if (state && state.activePayload && state.activePayload.length > 0) {
      const dataIndex = state.activePayload[0].payload.index;
      setZoomRangeStart(dataIndex);
    }
    console.log(
      "MouseDown: Zoom Start=" + zoomRangeStart + " , Zoom End=" + zoomRangeEnd
    );
  };

  // New function to handle mouse up event on the chart
  const handleMouseUp = (state) => {
    if (state && state.activePayload && state.activePayload.length > 0) {
      const dataIndex = state.activePayload[0].payload.index;
      if (zoomRangeStart !== null && dataIndex >= zoomRangeStart) {
        setZoomRangeEnd(dataIndex);
      }
    }
    console.log(
      "MouseUp: Zoom Start=" + zoomRangeStart + " , Zoom End=" + zoomRangeEnd
    );
  };

  // Adjusted zoom in function
  const zoomIn = () => {
    if (zoomRangeStart !== null && zoomRangeEnd !== null) {
      console.log(
        "ZoomIn: Zoom Start=" + zoomRangeStart + " , Zoom End=" + zoomRangeEnd
      );
      setXAxisDomain({ start: zoomRangeStart, end: zoomRangeEnd });
    }
    setZoomRangeStart("dataMin");
    setZoomRangeEnd("dataMax");
  };

  // Adjusted zoom out function
  const zoomOut = () => {
    setXAxisDomain({ start: "dataMin", end: "dataMax" });
    setZoomRangeStart("dataMin");
    setZoomRangeEnd("dataMax");
  };

  const updateAxisDomain = () => {
    let newLeftMin = Infinity;
    let newLeftMax = -Infinity;
    let newRightMin = Infinity;
    let newRightMax = -Infinity;

    potentialProperties.forEach((property) => {
      if (selectedYAxisValues[property]) {
        LogDataArray.forEach((data) => {
          const value = data[property];
          if (value === null || value === undefined || !Number.isFinite(Number(value))) {
            return;
          }
          const numericValue = Number(value);
          const assignedAxis = axisSelection[property] || "left";
          if (assignedAxis === "left") {
            newLeftMin = Math.min(newLeftMin, numericValue);
            newLeftMax = Math.max(newLeftMax, numericValue);
          } else if (assignedAxis === "right") {
            newRightMin = Math.min(newRightMin, numericValue);
            newRightMax = Math.max(newRightMax, numericValue);
          }
        });
      }
    });

    setLeftAxisDomain(
      newLeftMin === Number.MAX_VALUE
        ? ["auto", "auto"]
        : [newLeftMin, newLeftMax]
    );

    setRightAxisDomain(
      newRightMin === Number.MAX_VALUE
        ? ["auto", "auto"]
        : [newRightMin, newRightMax]
    );
  };

  // This determines if the value is displayed or not
  const handleCheckboxChange = (property) => {
    setSelectedYAxisValues((prevState) => ({
      ...prevState,
      [property]: !prevState[property],
    }));
  };

  // This determines which Y-axis the values are shown on
  const handleAxisChange = (property, axis) => {
    console.log(property + "," + axis);
    setAxisSelection((prev) => ({
      ...prev,
      [property]: axis,
    }));
  };

  const handleShowEvents = (showevents) => {
    setShowEvents(!showevents);
  };

  // Handler for toggling value dots
  const handleToggleValueDots = () => {
    setShowValueDots(!showValueDots);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Render tooltip content for event lines
      if (payload[0].payload.event !== "") {
        console.log("At event line");
        return (
          <div className="customTooltip">
            <p className="tooltipXValue">{`Time: ${
              payload[0].payload.date + " " + payload[0].payload.time
            }`}</p>
            <p className="tooltipEvent">{`${payload[0].payload.event}`}</p>
            {payload.map((p) => (
              <p className="tooltipValue" key={p.name}>
                {`${p.name}: ${p.value}`}
              </p>
            ))}
          </div>
        );
      }

      // Render tooltip content for data points
      return (
        <div className="customTooltip">
          <p className="tooltipXValue">{`Time: ${
            payload[0].payload.date + " " + payload[0].payload.time
          }`}</p>
          {payload.map((p) => (
            <p className="tooltipValue" key={p.name}>
              {`${p.name}: ${p.value}`}
            </p>
          ))}
        </div>
      );
    }

    return null;
  };
  // #endregion Chart view

  // #region Heat Map
  const handleHeatmapCellClick = (date, hour) => {
    setShowViewChartButton(false); // Show the view chart button
    // Filter events that match the clicked date and hour (hour from the 'time' field)
    const selectedHeatmapCell = heatMapData.filter((event) => {
      if (event.date === date && event.hour === hour) return event;
    });

    // Only update eventDetails if there are matching events
    if (selectedHeatmapCell.length > 0) {
      setEventDetails(selectedHeatmapCell[0]); // Set the event details
      setShowChart(false); // Reset chart visibility
      setLogDataArray([]); // Clear previous log data
      setSelectedYAxisValues({}); // Reset selected Y-axis values
      setAxisSelection({}); // Clear axis selection settings
      setZoomRangeStart("dataMin"); // Reset zoom start range
      setZoomRangeEnd("dataMax"); // Reset zoom end range
      setShowEvents(true); // Reset show events toggle
      setShowValueDots(false); // Reset show value dots toggle
      setShowViewChartButton(true); // Show the view chart button
    } else {
      setEventDetails(null); // Set to null if no events match
      setShowViewChartButton(false); // Hide the view chart button
    }
  };

  const handleViewDataButton = () => {
    if (buttonsEnabled) {
      // Logic to view data based on selected rows
      console.log("View Data clicked with selected rows:", selectedRows);
    }
  };

  const handleViewChartButton = () => {
    if (buttonsEnabled) {
      // Logic to view chart based on selected rows
      console.log("View Chart clicked with selected rows:", selectedRows);
    }
  };
  // #endregion Heat Map

  return (
    <div className="debugHomeContainer">
      <div className="debugHomeHeader">
        <div className="debugHomeHeaderTitle">
          <div className="debugHomeProductType">{product.product_type}</div>
          <div className="debugHomeProductSerialNumber">
            {product.product_serial_number}
          </div>
        </div>
        <div className="debugHomeFunctionButtons">
          <div className="debugHomeNavigateHome">
            <button
              className="navigationButton"
              onClick={() => handleNavigateHome()}
            >
              <FontAwesomeIcon icon={faHouse} />
              My Products
            </button>
          </div>
        </div>
      </div>

      <div className="debugHomeContent">
        {/* #region Drop down selection - allows users to select type and either heatmap(+optional chart) or timeline charts */}
        <div className="debugHomeContentDropdown">
          <select
            className="debugHomeDropdown"
            value={selectedDataType}
            onChange={(e) => {
              setSelectedDataType(e.target.value);
              setEventDetails(null);
              setDataAvailable(false);
              setShowChart(false);
            }}
          >
            <option value="0">Events</option>
            <option value="1">Mode changes</option>
            <option value="2">Charge Stage changes</option>
            <option value="3">Generator Status changes</option>
            <option value="4">AC Status changes</option>
          </select>
          <select
            className="debugHomeViewDropdown"
            value={selectedView}
            onChange={(e) => setSelectedView(e.target.value)}
          >
            <option value="0">Heatmap</option>
            <option value="1">Timeline</option>
          </select>
        </div>
        {/* #endregion Drop down selection */}

        {/* #region Heatmap and optional Chart view */}
        {dataAvailable && parseInt(selectedView) === 0 && (
          <>
            <div className="debugHomeHeatMapContainer">
              {/* Heatmap Grid is always shown for selected view == 0 */}
              <div className="debugHomeHeatMap">
                <HeatMapGrid
                  heatMapData={heatMapData}
                  onCellClick={handleHeatmapCellClick}
                />
                <div className="debugHomeHeatMapChartButton">
                  {showViewChartButton ? (
                    <button
                      className="debugHomeViewChartButton"
                      onClick={handleViewChart}
                    >
                      <FontAwesomeIcon icon={faChartLine} /> View Chart for Date:{eventDetails.date} Hour:{eventDetails.hour}
                    </button>
                  ) : (
                    eventDetails ? (
                      <div>Date:{eventDetails.date} Hour:{eventDetails.hour} changes={eventDetails.value}</div>
                    ) : (
                      <div>Select a heatmap cell to show the view chart</div>
                    )
                  )}
                </div>
              </div>

              {fetchingChartData && (
                <div className="debugHomeLoadingChart">
                  Fetching chart data ...
                </div>
              )}


              {/* #region Chart */}

              {showChart && !fetchingChartData && (
                <div className="debugHomeChart">
                  <div className="debugHomeChartContent">
                    <div className="zpButtonCollection">
                      <button className="zpButton" onClick={zoomIn}>
                        Zoom In
                      </button>
                      <button className="zpButton" onClick={zoomOut}>
                        Zoom Reset
                      </button>
                    </div>
                    <div className="debugHomeChartScrollWrapper">
                      <div className="debugHomeChart">
                        <LineChart
                          width={1200}
                          height={550}
                          data={LogDataArray}
                          margin={{
                            top: 10,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                          onMouseDown={handleMouseDown}
                          onMouseUp={handleMouseUp}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            allowDataOverflow
                            dataKey="index"
                            type="number"
                            domain={[xAxisDomain.start, xAxisDomain.end]}
                            tickFormatter={(index) => {
                              return LogDataArray[index]?.time ?? "";
                            }}
                            angle={-60} // Rotate the labels to -60 degrees
                            textAnchor="end" // Aligns the text towards the end of the anchor point
                            height={70} // Increase height to accommodate angled labels
                          />
                          <YAxis
                            yAxisId="left"
                            orientation="left"
                            domain={leftAxisDomain}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={rightAxisDomain}
                          />
                          {zoomRangeStart !== null && zoomRangeEnd !== null && (
                            <ReferenceArea
                              x1={zoomRangeStart}
                              x2={zoomRangeEnd}
                              yAxisId="left"
                              strokeOpacity={0.25}
                              stroke="red"
                              fill="lightpink"
                              fillOpacity={0.25}
                            />
                          )}
                          <Legend />
                          <Tooltip content={<CustomTooltip />} />
                          {potentialProperties.map(
                            (property) =>
                              selectedYAxisValues[property] && (
                                <Line
                                  key={property}
                                  yAxisId={axisSelection[property] || "left"}
                                  type="monotone"
                                  dataKey={property}
                                  stroke={propertyColors[property] || "#8884d8"}
                                  dot={showValueDots} // Added dot prop based on the new state
                                  animationDuration={300}
                                />
                              )
                          )}
                          {showEvents && renderEventLines(LogDataArray)}
                        </LineChart>
                      </div>
                      <div className="debugHomeChartChecklist">
                        {potentialProperties.map((property) => (
                          <div
                            className="debugHomeChartChecklistContainer"
                            key={property}
                          >
                            <input
                              type="checkbox"
                              checked={selectedYAxisValues[property] || false}
                              onChange={() => handleCheckboxChange(property)}
                            />
                            <select
                              className="debugHomeChartAxisSelection"
                              onChange={(e) =>
                                handleAxisChange(property, e.target.value)
                              }
                            >
                              <option value="left">Left Axis</option>
                              <option value="right">Right Axis</option>
                            </select>
                            <div style={{ color: propertyColors[property] }}>
                              {property}
                            </div>
                          </div>
                        ))}
                        <div className="debugHomeChartShowEvents">
                          <input
                            type="checkbox"
                            checked={showEvents || false}
                            onChange={() => handleShowEvents(showEvents)}
                          />
                          <span>Show Events</span>
                        </div>
                        <div className="debugHomeChartShowValueDots">
                          <input
                            type="checkbox"
                            checked={showValueDots || false}
                            onChange={handleToggleValueDots}
                          />
                          <span>Show Value Dots</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* #endregion Chart */}
            </div>
          </>
        )}
        {/* #endregion Heatmap and Chart */}

        {/* #region Timeline and Table view */}
        {dataAvailable && parseInt(selectedView) === 1 && (
          <>
            {apexTimelineData.length > 0 ? (
              <div className="debugHomeTimelineContainer">
                <div className="debugHomeTimelineChart">
                  {/* Based on: https://codesandbox.io/p/sandbox/recharts-timeline-hv662l 
                    (From the article: https://github.com/recharts/recharts/issues/4038#issuecomment-2174967302) 
                    Specifically implements the TimelineGraphedFlatData component in the example */}
                  <ResponsiveContainer width={1200} height={350}>
                    <ReactApexChart
                      options={apexTimelineOptions}
                      series={apexTimelineData}
                      type="rangeBar"
                      height={350}
                    />
                  </ResponsiveContainer>
                </div>

                <div className="debugDataTableContent">
                  <div className="debugDataTableHeader">
                    <button
                      onClick={() => {
                        setSelectedRows([]);
                        setSelectionMessage("");
                        setButtonsVisible(false);
                        setButtonsEnabled(false);
                      }}
                    >
                      Clear Selection
                    </button>
                    {selectionMessage && (
                      <div className="selectionMessage">{selectionMessage}</div>
                    )}
                    {buttonsVisible && (
                      <div className="actionButtons">
                        <button
                          style={{ marginRight: 5 }}
                          onClick={() => handleViewDataButton()}
                          disabled={!buttonsEnabled}
                        >
                          View Data
                        </button>
                        <button
                          onClick={() => handleViewChartButton()}
                          disabled={!buttonsEnabled}
                        >
                          View Chart
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="debugDataTableScrollWrapper">
                    <table className="debugDataTable">
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>Start</th>
                          <th>End</th>
                          <th>Duration</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apexTimelineData.length > 0 ? (
                          apexTimelineData.map((entry, index) => {
                            const startTime = new Date(entry.data[0].y[0]);
                            const endTime = new Date(entry.data[0].y[1]);
                            const duration = (
                              (entry.data[0].y[1] - entry.data[0].y[0]) /
                              60000
                            ).toFixed(2);

                            return (
                              <tr key={index}>
                                <td>
                                  {/*                                   <input
                                    type="checkbox"
                                    checked={selectedRows.includes(index)}
                                  /> */}
                                </td>
                                <td>{startTime.toLocaleString()}</td>
                                <td>{endTime.toLocaleString()}</td>
                                <td>{duration} mins</td>
                                <td>{entry.name}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="5">No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div>No data available</div>
            )}
          </>
        )}
        {/* #endregion Timeline */}
      </div>
    </div>
  );
};

export default DebugHome;
